/* istanbul ignore file */
// All logic is located in helpers
import { ListProvider } from './ListContext';
import { Filter } from './filter/FilterContext';
import { SimpleListProps } from './SimpleList';
import useListCrud from './builder/useListCrud';
import useListParams from './builder/useListParams';
import useListDataLoading from './builder/useListDataLoading';
import useListUrlStoring from './builder/useListUrlStoring';
import useListLocalStorageStoring from './builder/useListLocalStorageStoring';
import useListDataUpdating from './builder/useListDataUpdating';
import useListParamsConcatenation from './builder/useListParamsConcatenation';

export const LIST_PAGE_SIZE = 30;

export interface ListProps extends SimpleListProps {
  defaultSortOrder?: string;
  defaultSortField?: string;
  defaultFilter?: Filter;
  pageSize?: number;
  onRemove?: (params: StringOrNumber) => void;
  localStorageKey?: string;
  uncontrolledFilters?: string[];
  shouldSaveUncontrolledFilters?: boolean;
  autoUpdateTime?: number;
  disableUrlUpdate?: boolean;
}

const List = ({
  getDataMethod,
  children,
  localStorageKey,
  uncontrolledFilters,
  shouldSaveUncontrolledFilters,
  pageSize = LIST_PAGE_SIZE,
  defaultSortOrder = '',
  defaultSortField = '',
  defaultFilter = {},
  onRemove = () => {},
  autoUpdateTime,
  disableUrlUpdate = false,
}: ListProps) => {
  const { params: urlParams, updateParamsInUrl } = useListUrlStoring();
  const { params: storageParams, updateParamsInLocalStorage } = useListLocalStorageStoring({
    localStorageKey,
    uncontrolledFilters,
    defaultFilter,
    shouldSaveUncontrolledFilters,
  });
  const { params: initialParams } = useListParamsConcatenation({ urlParams, localStorageParams: storageParams });

  const onParamsChangeCallbacks = disableUrlUpdate
    ? [updateParamsInLocalStorage]
    : [updateParamsInUrl, updateParamsInLocalStorage];

  const { params, sortBy, searchBy, filterBy, goToPage, resetParamsToDefault } = useListParams({
    initialParams,
    defaultSortOrder,
    defaultSortField,
    defaultFilter,
    onParamsChangeCallbacks,
  });

  const { loading, loadData, setLoading } = useListDataLoading({ getDataMethod, resetParamsToDefault });

  const { data, reloadData } = useListDataUpdating({ loadData, params, pageSize, autoUpdateTime });
  const listCrud = useListCrud({ onRemove, inputData: data });

  const contextData = {
    ...params,
    filterBy,
    sortBy,
    goToPage,
    searchBy,
    loading,
    setLoading,
    reloadData,
    uncontrolledFilters,
    ...listCrud,
  };

  return <ListProvider value={contextData}>{children}</ListProvider>;
};

export default List;
